import { brand } from '../../theme/themePrimitives';
import PipelineActionDropdown from './action_dropdown';
import { PipelineObject } from '../../params/pipeline';
import SelectPipeline from './select_pipeline';
import { setAddingDeal } from '../../redux/slices/deal';
import { settingSelectedStage } from '../../redux/slices/stage';
import { toggleBulkEditMode } from '../../redux/slices/pipeline';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { ArrowBack, ArrowForward, Close, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { bulkDealTransferThunk, fetchPipelineTemplatesThunk } from '../../redux/thunks/pipeline';
import { getTotalDeals, handlePipelineChange } from '../../helper/pipelineHelper';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: PipelineHeader Component
 * @paragraph: This component renders the header for the pipeline view, including the pipeline dropdown, total deals and values, and action buttons for adding pipelines or deals.
 * @param: {(value: boolean) => void} setShowPopup - Function to toggle the modal for adding a new pipeline.
 */

interface PipelineHeaderProps {
    scrollPipeline: (direction: 'left' | 'right') => void;
    canScrollLeft: boolean;
    canScrollRight: boolean;
    setShowPopup: (value: boolean) => void;
    setAddStage: (value: boolean) => void;
}
const PipelineHeader: React.FC<PipelineHeaderProps> = ({ setShowPopup, scrollPipeline, canScrollLeft, canScrollRight, setAddStage }) => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * anchorEl: {null | HTMLElement} - React state for popup on hover.
     * pipelines/selectedPipeline/loaders: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { pipelines, selectedPipeline, loaders, bulkEditMode, selectedStageForBulkEdit, selectedDeals } = useSelector((state: RootState) => state.pipeline);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const disabled = loaders.isCreating || loaders.isDeleting || loaders.isLoading || loaders.isUpdating;
    const totalDeals = getTotalDeals(selectedPipeline as PipelineObject);
    // const totalValue = getTotalValues(selectedPipeline as PipelineObject);

    /**
     * @heading3: Functions
     * @heading4: handleButtonClick
     * @paragraph: Opens the dropdown menu for adding pipelines or deals.
     * @param: {React.MouseEvent<HTMLButtonElement>} event - The button click event.
     */
    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * @heading4: handleMenuClose
     * @paragraph: Closes the dropdown menu.
     */
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const addDealHandler = () => {
        dispatch(setAddingDeal(true));
        dispatch(settingSelectedStage(null));
    };

    const saveChangesHandler = async () => {
        try {
            await dispatch(bulkDealTransferThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
        } catch (e) {
            //
        }
    };

    const addStageHandler = () => {
        dispatch(settingSelectedStage(null));
        setAnchorEl(null);
        setAddStage(true);
    };

    const addPipelineHandler = () => {
        dispatch(fetchPipelineTemplatesThunk()).unwrap();
        setShowPopup(true);
    };

    /**
     * @heading4: Render DefaultBadge
     * @paragraph: Displays a "Default" badge next to the pipeline name if it is the default pipeline.
     * @param: {Object} props - Props containing `isDefault` to determine if the badge should be shown.
     */
    const DefaultBadge: React.FC<{ isDefault: boolean }> = ({ isDefault }) => {
        if (!isDefault) return null;
        return (
            <Box
                component="span"
                ml={2}
                p={1}
                borderRadius={1}
                sx={{ backgroundColor: brand[100] }}
            >
                Default
            </Box>
        );
    };

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            {/* Render Pipeline Selection and total deals and default Box */}
            <Box display="flex" gap="20px" alignItems="center">
                {pipelines.length !== 0 && (
                    <SelectPipeline
                        pipelines={pipelines}
                        onChange={(e) => handlePipelineChange(e, dispatch)}
                        value={selectedPipeline ? selectedPipeline.id : ''}
                        error={false}
                        disabled={disabled}
                        sx={{
                            mb: 0
                        }}
                    />
                )}
                <Typography color={brand[300]} whiteSpace="nowrap" variant="body1" fontWeight={600}>
                    {/* {totalDeals} - ${totalValue} */}
                    {disabled ? '0 Deal' : totalDeals} - ${selectedPipeline?.deal_default_value}
                    <DefaultBadge isDefault={selectedPipeline?.is_default === 1} />
                </Typography>
            </Box>
            {/* Action Button with Popup Menu */}
            {bulkEditMode
                ? (<Box display="flex" gap={2} alignItems="center">
                    <Button variant="outlined" size="large" color="secondary" startIcon={<Close />} onClick={() => dispatch(toggleBulkEditMode(false))}>
                        Cancel Edit
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        disabled={!selectedPipeline?.pipeline_stages?.some((item) => item.id === selectedStageForBulkEdit) || selectedDeals.length === 0 || loaders.isLoading}
                        onClick={saveChangesHandler}
                    >
                        Save Changes
                    </Button>
                </Box>)
                : (<Box display="flex" gap={2}>
                    {canScrollLeft && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => scrollPipeline('left')}
                            startIcon={<ArrowBack />}
                        >
                            Move Left
                        </Button>
                    )}
                    {canScrollRight && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => scrollPipeline('right')}
                            endIcon={<ArrowForward />}
                        >
                            Move Right
                        </Button>
                    )}
                    {!bulkEditMode && pipelines.length > 0 && <PipelineActionDropdown />}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleButtonClick}
                        disabled={disabled}
                        endIcon={<KeyboardArrowDownOutlined />}
                    >
                        Add
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            'aria-labelledby': 'add-pipeline-button'
                        }}
                    >
                        <MenuItem onClick={addPipelineHandler}>
                            Add New Pipeline
                        </MenuItem>
                        {selectedPipeline && <MenuItem onClick={addStageHandler}>
                            Add New Stage
                        </MenuItem>}
                        {selectedPipeline && <MenuItem onClick={addDealHandler}>
                            Add New Deal
                        </MenuItem>}
                    </Menu>
                </Box>)}
        </Box>
    );
};
export default PipelineHeader;
